import React from 'react';
import { Input, Select, DatePicker, Checkbox } from 'components';
import { RootState } from 'reducers';
import { Globals, Constants, Colors } from 'utils';
import moment from 'moment';
import { RevisionService, MembershipService, GeneralService } from 'services';
import { connect } from 'react-redux';
import { CloseIcon, BackIcon, CopyIcon } from 'assets/icons';
import { StripeImage } from 'assets/img';
import axios from 'axios';

interface Props extends RootState {
  onClose?: any;
  price: number;
  title: string;
  onSuccess?: any;
}

const STEPS = {
  ORDER: 3,
  PAYMENT: 4,
};

const to2 = (n: number) => Math.round(n * 100) / 100;

class ModalPayment extends React.Component<Props> {
  formRef = React.createRef<HTMLDivElement>();

  state = {
    form: {
      payment_method_id: '',
      expiration_month: '',
      expiration_year: '',
      number: '',
      cvv: '',
      bank_id: '',
      phone: '',
      phonecode_id: '',
      date: '',
      reference: '',
      sypago_bank_code: '',
      sypago_phone: '',
      sypago_phonecode: '',
      sypago_document_type: '',
      sypago_document: '',
      sypago_otp: '',
      sypago_name: '',
      is_checked: false,
    },
    step: STEPS.ORDER,
    banks: [],
    months: [],
    years: [],
    bank_data: null,
    iva: 0,
    conversion: 0,
    sypago_banks: [],
    payment_methods: [],
    phonecodes: [],
    document_types: [],
    otp_send: false,
    submitting: false,
  };

  async componentDidMount() {
    this.load();
    this.loadData();

    let months = [];
    let years = [];

    for (let i = 1; i <= 12; i++) {
      months.push({
        value: moment(i, 'M').format('MM'),
        name: moment(i, 'M').format('MM'),
      });
    }

    for (let i = 0; i < 10; i++) {
      years.push({
        value: moment().add(i, 'years').format('YYYY'),
        name: moment().add(i, 'years').format('YYYY'),
      });
    }

    await this.setState({
      months,
      years,
    });
  }

  loadData = async () => {
    const res: any = await MembershipService.getBanks({
      withoutLoading: true,
    });
    this.setState({
      banks: res.banks || [],
      bank_data: res.bank_data,
      sypago_banks: res.sypago_banks || [],
    });
  };

  load = async () => {
    const res: any = await GeneralService.getSelects({
      user_id: this.props.user?.id,
      iva: true,
      conversion: true,
      payment_methods: true,
      document: true,
      phoneCode: true,
    });

    this.setState({
      iva: res.iva?.value || 0,
      payment_methods: res.payment_methods || [],
      conversion: res.conversion?.amount || 0,
      document_types: res.document_types || [],
      phonecodes: res.phonecodes || [],
      form: {
        ...this.state.form,
        sypago_document_type: res.document_types.length > 0 ? res.document_types[0].name : '',
        sypago_phonecode: res.phonecodes.length > 0 ? res.phonecodes[0].name : '',
        phonecode_id: res.phonecodes.length > 0 ? res.phonecodes[0].id : '',
      },
    });
  };

  change = (e: any, callback = () => {}, restart_otp = true) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
        otp_send: !restart_otp,
      },
      callback,
    );
  };

  selectPaymentMethod = (id: any) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          payment_method_id: id,
        },
      },
      () => {
        setTimeout(() => {
          this.formRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 50);
      },
    );
  };

  submit = async () => {
    const { bank_id, phone, date, phonecode_id, is_checked } = this.state.form;
    const { subtotal, iva_total, total_factura, igtf_total, total } = this.getTotal();

    const fiscalOverrides = {
      __subtotal: subtotal,
      __iva_total: iva_total,
      __igtf_total: igtf_total,
      __total: total,
    };

    if (!is_checked) {
      Globals.showError('Por favor, declare que el pago proviene de manera lícita');
      return;
    }

    if (this.state.form.payment_method_id == Constants.PAYMENT_METHODS.PAYMENT_MOBILE.toString()) {
      if (!bank_id) {
        Globals.showError('Por favor, ingrese el banco de origen');
        return;
      }
      if (!phonecode_id) {
        Globals.showError('Por favor, ingrese código del teléfono');
        return;
      }
      if (!phone) {
        Globals.showError('Por favor, ingrese el teléfono');
        return;
      }
      if (!date) {
        Globals.showError('Por favor, ingrese la fecha');
        return;
      }

      this.setState({ submitting: true });
      try {
        await this.props.onSuccess({
          ...this.state.form,
          paymentMethod: Constants.PAYMENT_METHODS.PAYMENT_MOBILE,
          date:
            this.state.form.date && moment(this.state.form.date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          ...fiscalOverrides,
        });
      } finally {
        this.setState({ submitting: false });
      }
      return;
    }

    if (this.state.form.payment_method_id == Constants.PAYMENT_METHODS.STRIPE.toString()) {
      if (!this.state.form.number) {
        Globals.showError('Por favor, ingrese el número de su tarjeta');
        return;
      }
      if (!this.state.form.expiration_month || !this.state.form.expiration_year) {
        Globals.showError('Por favor, ingrese la fecha de vencimiento de su tarjeta');
        return;
      }
      if (!this.state.form.cvv) {
        Globals.showError('Por favor, ingrese el código secreto de su tarjeta');
        return;
      }
      if (
        moment().add(1, 'month').startOf('month') >
        moment(
          `01-${this.state.form.expiration_month}-${this.state.form.expiration_year}`,
          'DD-MM-YYYY',
        )
      ) {
        Globals.showError('Lo sentimos, la fecha de vencimiento no es válida');
        return;
      }

      const params = new URLSearchParams({
        'card[exp_month]': this.state.form.expiration_month,
        'card[exp_year]': this.state.form.expiration_year,
        'card[number]': this.state.form.number,
        'card[cvc]': this.state.form.cvv,
      }).toString();

      try {
        Globals.setLoading();
        const response = await axios.request({
          method: 'POST',
          url: process.env.REACT_APP_STRIPE_URL + 'tokens?' + params,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: 'Bearer ' + process.env.REACT_APP_STRIPE_KEY,
          },
        });

        Globals.quitLoading();
        this.setState({ submitting: true });
        try {
          await this.props.onSuccess({
            ...this.state.form,
            paymentMethod: Constants.PAYMENT_METHODS.STRIPE,
            token: response?.data?.id,
            date:
              this.state.form.date &&
              moment(this.state.form.date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
            ...fiscalOverrides,
          });
        } finally {
          this.setState({ submitting: false });
        }
      } catch (e) {
        console.log(e);
        Globals.quitLoading();
        Globals.showError('Lo sentimos, no se pudo procesar el pago');
      }
      return;
    }

    if (this.state.form.payment_method_id == Constants.PAYMENT_METHODS.SYPAGO.toString()) {
      const {
        sypago_document,
        sypago_document_type,
        sypago_bank_code,
        sypago_phonecode,
        sypago_phone,
        sypago_otp,
      } = this.state.form;

      if (
        !sypago_document ||
        !sypago_document_type ||
        !sypago_bank_code ||
        !sypago_phonecode ||
        !sypago_phone
      ) {
        Globals.showError('Por favor, ingrese todos los datos para continuar');
        return;
      }
      if (sypago_phone.length < 7) {
        Globals.showError('El campo teléfono debe tener 7 dígitos');
        return;
      }

      if (!this.state.otp_send) {
        try {
          await MembershipService.generateOtp({
            ...this.state.form,
            amount: total,
          });
          this.setState({ otp_send: true });
        } catch (e) {
          console.error(e);
          Globals.showError('No fue posible solicitar la clave OTP');
        }
        return;
      }

      if (!sypago_otp) {
        Globals.showError('Por favor, ingrese su clave OTP');
        return;
      }

      this.setState({ submitting: true });
      try {
        await this.props.onSuccess({
          ...this.state.form,
          paymentMethod: Constants.PAYMENT_METHODS.SYPAGO,
          date:
            this.state.form.date && moment(this.state.form.date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          ...fiscalOverrides,
        });
      } finally {
        this.setState({ submitting: false });
      }
      return;
    }
  };

  clipboard = (e: any, text: string) => {
    e.preventDefault();
    navigator.clipboard.writeText(text);
  };

  getTotal = () => {
    const subtotal = Number(this.props.price || 0);
    const iva_total = subtotal * (Number(this.state.iva) / 100);
    const total_factura = subtotal + iva_total;

    const isStripe =
      this.state.form.payment_method_id?.toString() === Constants.PAYMENT_METHODS.STRIPE.toString();
    const igtf_total = isStripe ? total_factura * (Constants.TAXES.IGTF / 100) : 0;

    const total = total_factura + igtf_total;

    // Desglose en bolívares (solo relevante para Pago Móvil/SyPago, VEF): el IVA en Bs
    // se DERIVA de (total_bs - subtotal_bs) en vez de recalcularse independiente, para
    // que siempre cuadre exactamente con `total_bs` -- que es el mismo valor que se usa
    // para cobrar -- y no se pierda precisión por doble redondeo en montos chicos (mismo
    // criterio aplicado en la app mobile).
    const conversion = Number(this.state.conversion || 0);
    const subtotal_bs = to2(subtotal * conversion);
    const total_bs = to2(total * conversion);
    const iva_bs = to2(total_bs - subtotal_bs);

    return {
      subtotal: to2(subtotal),
      iva_total: to2(iva_total),
      total_factura: to2(total_factura),
      igtf_total: to2(igtf_total),
      total: to2(total),
      subtotal_bs,
      iva_bs,
      total_bs,
    };
  };

  back = () => {
    const document_types: any = this.state.document_types;
    const phonecodes: any = this.state.phonecodes;
    switch (this.state.step) {
      case STEPS.PAYMENT:
        this.setState({
          form: {
            ...this.state.form,
            payment_method_id: '',
            expiration_month: '',
            expiration_year: '',
            number: '',
            cvv: '',
            bank_id: '',
            phone: '',
            date: '',
            reference: '',
            sypago_document_type: document_types.length > 0 ? document_types[0].name : '',
            sypago_phonecode: phonecodes.length > 0 ? phonecodes[0].name : '',
            sypago_bank_code: '',
            sypago_phone: '',
            sypago_document: '',
            sypago_otp: '',
            sypago_name: '',
          },
          otp_send: false,
          step: STEPS.ORDER,
        });
        break;
    }
  };

  render() {
    const { step } = this.state;
    const bank_data: any = this.state.bank_data;
    const { subtotal, iva_total, total, igtf_total, subtotal_bs, iva_bs, total_bs } =
      this.getTotal();
    const isStripe =
      this.state.form.payment_method_id?.toString() === Constants.PAYMENT_METHODS.STRIPE.toString();
    const showBsBreakdown =
      this.state.form.payment_method_id?.toString() ===
        Constants.PAYMENT_METHODS.PAYMENT_MOBILE.toString() ||
      this.state.form.payment_method_id?.toString() === Constants.PAYMENT_METHODS.SYPAGO.toString();

    return (
      <div id='modal-payment'>
        {step == STEPS.ORDER && (
          <div className='payment-content'>
            <p className='title'>Mi pedido</p>

            <StepsContainer step={step} />

            <div className='container-mode'>
              <h3>{this.props.title}</h3>
              <div className='container-total'>
                <div className='row row-total'>
                  <div className='col-6'>
                    <div className='container-circle'>
                      <p>Fee de plataforma</p>
                    </div>
                  </div>
                  <div className='col-6 text-right'>
                    <p className='bold'>{Globals.formatMiles(this.props.price)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className='fee-notice'>
              <div className='fee-notice__icon'>&#9432;</div>
              <div className='fee-notice__body'>
                <p className='fee-notice__title'>Pago a la plataforma</p>
                <p className='fee-notice__text'>
                  Este monto es el fee de acceso al servicio. Los honorarios del abogado se acuerdan
                  y pagan directamente con él, fuera de la app.
                </p>
              </div>
            </div>

            <div className='container-total' style={{ paddingBottom: '10px' }}>
              <div className='row row-total'>
                <div className='col-6'>
                  <p>Sub-total</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(subtotal)}</p>
                </div>
              </div>
              <div className='separator' />
              <div className='row row-total'>
                <div className='col-6'>
                  <p>Impuesto</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(iva_total)}</p>
                </div>
              </div>
              <div className='total-blue'>
                <div className='row'>
                  <div className='col-6'>
                    <p className='bold'>Total</p>
                  </div>
                  <div className='col-6 text-right'>
                    <p className='bold'>{Globals.formatMiles(total)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className='text-center'>
              <button
                className='btn btn-next'
                onClick={() => this.setState({ step: STEPS.PAYMENT })}
              >
                Pagar
              </button>
            </div>
          </div>
        )}

        {step == STEPS.PAYMENT && (
          <div className='payment-content'>
            <button className='btn btn-back' onClick={() => this.back()}>
              <img src={BackIcon} />
            </button>

            <p className='title'>Mi pedido</p>

            <StepsContainer step={step} />

            <div className='container-total' style={{ paddingBottom: '10px' }}>
              <div className='row row-total'>
                <div className='col-6'>
                  <p>Sub-total</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(subtotal)}</p>
                </div>
              </div>
              <div className='separator' />
              <div className='row row-total'>
                <div className='col-6'>
                  <p>IVA</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(iva_total)}</p>
                </div>
              </div>

              {isStripe && (
                <>
                  <div className='separator' />
                  <div className='row row-total'>
                    <div className='col-6'>
                      <p>IGTF</p>
                    </div>
                    <div className='col-6 text-right'>
                      <p className='bold'>{Globals.formatMiles(igtf_total)}</p>
                    </div>
                  </div>
                </>
              )}

              <div className='total-blue' style={{ marginTop: '10px' }}>
                <div className='row'>
                  <div className='col-6'>
                    <p className='bold'>Total</p>
                  </div>
                  <div className='col-6 text-right'>
                    <p className='bold'>{Globals.formatMiles(total)}</p>
                  </div>
                </div>
              </div>
            </div>

            <p className='total'>{Globals.formatMiles(total)}</p>

            <div className='row row-payment-methods'>
              {this.state.payment_methods.map((item: any) => (
                <div className='col-md-6' key={item.id}>
                  <button
                    className={`btn btn-payment-method ${item.only_icon ? 'center' : ''} ${item.id == this.state.form.payment_method_id ? 'active' : ''}`}
                    style={{
                      backgroundColor: item.color || Colors.blue_light,
                    }}
                    onClick={() => this.selectPaymentMethod(item.id)}
                  >
                    <img src={Constants.BASE_PUBLIC + item.icon} />
                    {!item.only_icon && <p className='method-name'>{item.name}</p>}
                  </button>
                </div>
              ))}
            </div>

            {/* Ancla de scroll — el formulario aparece aquí */}
            <div ref={this.formRef}>
              {showBsBreakdown && (
                <div className='container-total bs-breakdown' style={{ paddingBottom: '10px' }}>
                  <div className='row row-total'>
                    <div className='col-6'>
                      <p>Costo del paquete</p>
                    </div>
                    <div className='col-6 text-right'>
                      <p className='bold'>{Globals.formatMiles(subtotal_bs, true, 'Bs.')}</p>
                    </div>
                  </div>
                  <div className='separator' />
                  <div className='row row-total'>
                    <div className='col-6'>
                      <p>IVA ({Number(this.state.iva || 0)}%)</p>
                    </div>
                    <div className='col-6 text-right'>
                      <p className='bold'>{Globals.formatMiles(iva_bs, true, 'Bs.')}</p>
                    </div>
                  </div>
                  <div className='total-blue' style={{ marginTop: '10px' }}>
                    <div className='row'>
                      <div className='col-6'>
                        <p className='bold'>Total a pagar</p>
                      </div>
                      <div className='col-6 text-right'>
                        <p className='bold'>{Globals.formatMiles(total_bs, true, 'Bs.')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {this.state.form.payment_method_id ==
                Constants.PAYMENT_METHODS.PAYMENT_MOBILE.toString() && (
                <div className='container-payment-mobile'>
                  <div className='row'>
                    <div className='col-md-12'>
                      <p className='bank'>
                        {bank_data?.bank?.name} - {bank_data?.bank?.code}
                      </p>
                    </div>
                    <div className='col-md-6'>
                      <p className='label-data'>Teléfono</p>
                    </div>
                    <div className='col-md-6 text-right'>
                      <div className='container-copy'>
                        <p className='bank-data'>
                          {bank_data?.phonecode?.name}
                          {bank_data?.phone}
                        </p>
                        <a
                          href='#'
                          onClick={(e: any) =>
                            this.clipboard(e, bank_data?.phonecode?.name + bank_data?.phone)
                          }
                        >
                          <img src={CopyIcon} />
                        </a>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <p className='label-data'>Cédula/RIF</p>
                    </div>
                    <div className='col-md-6 text-right'>
                      <div className='container-copy'>
                        <p className='bank-data'>
                          {bank_data?.document_type?.name}
                          {bank_data?.document}
                        </p>
                        <a
                          href='#'
                          onClick={(e: any) =>
                            this.clipboard(e, bank_data?.document_type?.name + bank_data?.document)
                          }
                        >
                          <img src={CopyIcon} />
                        </a>
                      </div>
                    </div>
                  </div>
                  <p className='subtitle-data'>Indique datos para realizar operación</p>
                  <div className='row'>
                    <div className='col-md-12'>
                      <Select
                        label='Banco'
                        color='white'
                        placeholder='Banco de origen'
                        name='bank_id'
                        onChange={this.change}
                        value={this.state.form.bank_id}
                        options={this.state.banks.map((item: any) => ({
                          value: item.id,
                          label: item.name,
                        }))}
                      />
                    </div>
                    <div className='col-md-6'>
                      <div className='container-select'>
                        <label>Teléfono</label>
                        <div>
                          <Select
                            color='white'
                            name='phonecode_id'
                            onChange={this.change}
                            value={this.state.form.phonecode_id}
                            options={this.state.phonecodes.map((item: any) => ({
                              value: item.id,
                              label: item.name,
                            }))}
                          />
                          <Input
                            maxLength={7}
                            type='number'
                            color='white'
                            value={this.state.form.phone}
                            name='phone'
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <DatePicker
                        label='Fecha'
                        placeholder='Fecha'
                        maxDate={moment().toDate()}
                        onChange={(text: string) => {
                          this.change({
                            target: { value: text, name: 'date' },
                          });
                        }}
                        value={this.state.form.date}
                      />
                    </div>
                  </div>
                  <Checkbox
                    label='Declaro que los fondos utilizados son de origen lícito'
                    checked={this.state.form.is_checked}
                    onChange={() =>
                      this.setState({
                        form: {
                          ...this.state.form,
                          is_checked: !this.state.form.is_checked,
                        },
                      })
                    }
                    name='is_checked'
                  />
                  <div className='text-center'>
                    <button
                      className='btn btn-next'
                      onClick={() => this.submit()}
                      disabled={this.state.submitting}
                    >
                      {this.state.submitting ? 'Procesando...' : 'Pagar'}
                    </button>
                  </div>
                </div>
              )}

              {this.state.form.payment_method_id == Constants.PAYMENT_METHODS.STRIPE.toString() && (
                <div className='container-stripe'>
                  <div className='text-center'>
                    <img src={StripeImage} />
                  </div>
                  <p className='subtitle-data'>Indique datos para realizar operación</p>
                  <div className='row'>
                    <div className='col-md-12'>
                      <Input
                        maxLength={16}
                        type='number'
                        color='white'
                        value={this.state.form.number}
                        name='number'
                        placeholder='Número de tarjeta'
                        onChange={this.change}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Select
                        color='white'
                        placeholder='Mes'
                        name='expiration_month'
                        onChange={this.change}
                        value={this.state.form.expiration_month}
                        options={this.state.months.map((item: any) => ({
                          value: item.value,
                          label: item.name,
                        }))}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Select
                        color='white'
                        placeholder='Año'
                        name='expiration_year'
                        onChange={this.change}
                        value={this.state.form.expiration_year}
                        options={this.state.years.map((item: any) => ({
                          value: item.value,
                          label: item.name,
                        }))}
                      />
                    </div>
                    <div className='col-md-12'>
                      <Input
                        maxLength={4}
                        type='password'
                        color='white'
                        value={this.state.form.cvv}
                        name='cvv'
                        placeholder='Código (CVC)'
                        onChange={this.change}
                      />
                    </div>
                  </div>
                  <Checkbox
                    label='Declaro que los fondos utilizados son de origen lícito'
                    checked={this.state.form.is_checked}
                    onChange={() =>
                      this.setState({
                        form: {
                          ...this.state.form,
                          is_checked: !this.state.form.is_checked,
                        },
                      })
                    }
                    name='is_checked'
                  />
                  <div className='text-center'>
                    <button
                      className='btn btn-next'
                      onClick={() => this.submit()}
                      disabled={this.state.submitting}
                    >
                      {this.state.submitting ? 'Procesando...' : 'Pagar'}
                    </button>
                  </div>
                </div>
              )}

              {this.state.form.payment_method_id == Constants.PAYMENT_METHODS.SYPAGO.toString() && (
                <div className='container-payment-mobile'>
                  <p className='subtitle-data'>Indique datos para realizar operación</p>
                  <div className='row'>
                    <div className='col-md-12'>
                      <Select
                        color='white'
                        label='Banco'
                        name='sypago_bank_code'
                        onChange={this.change}
                        value={this.state.form.sypago_bank_code}
                        options={this.state.sypago_banks.map((item: any) => ({
                          value: item.code || item.Code,
                          label: item.name || item.Name,
                        }))}
                      />
                    </div>
                    <div className='col-md-6'>
                      <div className='container-select'>
                        <label>Teléfono</label>
                        <div>
                          <Select
                            color='white'
                            name='sypago_phonecode'
                            onChange={this.change}
                            value={this.state.form.sypago_phonecode}
                            options={this.state.phonecodes.map((item: any) => ({
                              value: item.name,
                              label: item.name,
                            }))}
                          />
                          <Input
                            maxLength={7}
                            type='number'
                            color='white'
                            value={this.state.form.sypago_phone}
                            name='sypago_phone'
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='container-select'>
                        <label>Cédula/RIF</label>
                        <div>
                          <Select
                            color='white'
                            name='sypago_document_type'
                            onChange={this.change}
                            value={this.state.form.sypago_document_type}
                            options={this.state.document_types.map((item: any) => ({
                              value: item.name,
                              label: item.name,
                            }))}
                          />
                          <Input
                            maxLength={9}
                            type='number'
                            color='white'
                            value={this.state.form.sypago_document}
                            name='sypago_document'
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </div>
                    {this.state.otp_send && (
                      <div className='col-md-12'>
                        <p className='otp'>
                          Su banco se comunicará con usted para enviarle la clave OTP. Por favor,
                          ingrese su clave OTP:
                        </p>
                        <Input
                          maxLength={8}
                          type='number'
                          color='white'
                          value={this.state.form.sypago_otp}
                          name='sypago_otp'
                          placeholder='Clave OTP'
                          onChange={(e: any) => this.change(e, undefined, false)}
                        />
                      </div>
                    )}
                  </div>
                  <Checkbox
                    label='Declaro que los fondos utilizados son de origen lícito'
                    checked={this.state.form.is_checked}
                    onChange={() =>
                      this.setState({
                        form: {
                          ...this.state.form,
                          is_checked: !this.state.form.is_checked,
                        },
                      })
                    }
                    name='is_checked'
                  />
                  <div className='text-center'>
                    <button
                      className='btn btn-next'
                      onClick={() => this.submit()}
                      disabled={this.state.submitting}
                    >
                      {this.state.submitting
                        ? this.state.otp_send
                          ? 'Procesando...'
                          : 'Enviando...'
                        : this.state.otp_send
                          ? 'Pagar'
                          : 'Continuar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(ModalPayment);

const StepsContainer = (params: any) => {
  return (
    <div className='text-center'>
      <div className='container-steps'>
        <div className={`step ${params.step >= STEPS.ORDER ? 'active' : ''}`}>
          <p>Precio</p>
        </div>
        <div className='separator'></div>
        <div className={`step ${params.step >= STEPS.PAYMENT ? 'active' : ''}`}>
          <p>Método de pago</p>
        </div>
      </div>
    </div>
  );
};
